import { Link } from "@/lib/router-compat";
import { Menu } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import thiiaLogo from "@/assets/thiia-logo.svg";
import { trackNavClick } from "@/lib/analytics";

export default function PublicHeader() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const onNav = (label: string, to: string) => () => {
    trackNavClick("header", label, to);
    setOpen(false);
  };

  const navItems: { to: string; label: string }[] = [
    { to: "/landing#features", label: t("landing.navFeatures") },
    { to: "/pricing", label: t("landing.navPricing") },
    { to: "/use-cases", label: t("landing.navUseCases") },
    { to: "/security", label: t("landing.navSecurity") },
    { to: "/docs", label: t("landing.navDocs") },
    { to: "/contact", label: t("landing.navContact") },
  ];

  return (
    <nav className="fixed top-0 inset-x-0 z-50 glass border-b border-border/40">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-2">
        <Link to="/landing" className="flex items-center shrink-0" onClick={onNav("logo", "/landing")}>
          <img src={thiiaLogo} alt="Thiia" className="h-7 w-auto md:h-8" />
        </Link>

        {/* Desktop navigation */}
        <div className="hidden md:flex items-center gap-1">
          {navItems.slice(0, 5).map((item) => (
            <Button key={item.to} variant="ghost" asChild>
              <Link to={item.to} onClick={onNav(item.label, item.to)}>{item.label}</Link>
            </Button>
          ))}
        </div>

        {/* Desktop actions */}
        <div className="hidden md:flex items-center gap-2">
          <LanguageSwitcher />
          <Button variant="ghost" asChild>
            <Link to="/auth?mode=login" onClick={onNav(t("landing.navLogin"), "/auth?mode=login")}>
              {t("landing.navLogin")}
            </Link>
          </Button>
          <Button asChild>
            <Link to="/auth?mode=signup" onClick={onNav(t("landing.navTryFree"), "/auth?mode=signup")}>
              {t("landing.navTryFree")}
            </Link>
          </Button>
        </div>

        {/* Mobile actions */}
        <div className="flex md:hidden items-center gap-2">
          <Button asChild className="h-11 px-3">
            <Link to="/auth?mode=signup" onClick={onNav(t("landing.navTryShort"), "/auth?mode=signup")}>
              {t("landing.navTryShort")}
            </Link>
          </Button>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-11 w-11" aria-label={t("landing.navOpenMenu")}>
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[85%] max-w-sm flex flex-col pb-[calc(1.5rem+env(safe-area-inset-bottom))]"
            >
              <SheetHeader>
                <SheetTitle className="text-left">{t("landing.navMenu")}</SheetTitle>
              </SheetHeader>
              <nav className="mt-4 flex-1 overflow-y-auto flex flex-col">
                {navItems.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={onNav(item.label, item.to)}
                    className="flex items-center min-h-[56px] px-3 rounded-md text-[15px] text-foreground hover:bg-accent transition-colors"
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
              <div className="pt-4 space-y-2">
                <Button asChild className="w-full h-11">
                  <Link
                    to="/auth?mode=signup"
                    onClick={onNav(t("landing.navCreateAccount"), "/auth?mode=signup")}
                  >
                    {t("landing.navCreateAccount")}
                  </Link>
                </Button>
                <Button asChild variant="outline" className="w-full h-11">
                  <Link to="/auth?mode=login" onClick={onNav(t("landing.navLogin"), "/auth?mode=login")}>
                    {t("landing.navLogin")}
                  </Link>
                </Button>
                <div className="pt-2 flex justify-center">
                  <LanguageSwitcher />
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
}
